import React, { useState, useEffect } from 'react';
import {
  getEffectiveColor,
  getEffectiveIcon,
  getAlertTypeConfig,
} from './alertConfig';

const AlertPreview = ({ config }) => {
  const [showAlert, setShowAlert] = useState(true);

  // Auto-dismiss functionality
  useEffect(() => {
    if (config.autoDismiss && showAlert) {
      const delay = (config.autoDismissDelay || 3) * 1000;
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [config.autoDismiss, config.autoDismissDelay, showAlert]);

  // Reset alert when config changes significantly
  useEffect(() => {
    setShowAlert(true);
  }, [config.type, config.title, config.message]);

  const typeColor = getEffectiveColor(config);
  const typeIcon = getEffectiveIcon(config);
  const typeConfig = getAlertTypeConfig(config.type || 'success');

  const getAnimationClass = () => {
    if (!showAlert) return 'opacity-0';
    if (config.animationType === 'none') return '';

    switch (config.animationType || 'fade') {
      case 'fade':
        return 'animate-fade-in';
      case 'slide':
        return 'animate-slide-in';
      case 'scale':
        return 'animate-scale-in';
      case 'bounce':
        return 'animate-bounce-in';
      default:
        return '';
    }
  };

  const animationDuration = config.animationDuration || 0.3;
  const padding = config.padding || 24;
  const shadowEnabled = config.shadowEnabled !== false;
  const shadowRadius = config.shadowRadius || 20;
  const shadowOpacity = config.shadowOpacity ?? 0.1;
  const showIcon = config.showIcon !== false;
  const dismissible = config.dismissible !== false;
  const blurBackground = config.blurBackground !== false;
  const blurIntensity = config.blurIntensity || 10;

  // Fallback to defaults if empty
  const title = config.title || typeConfig.defaultTitle;
  const message = config.message || typeConfig.defaultMessage;
  const primaryButtonText = config.primaryButtonText || typeConfig.defaultPrimaryButton;
  const secondaryButtonText = config.secondaryButtonText || 'Cancel';
  const tertiaryButtonText = config.tertiaryButtonText || 'Delete';
  const backgroundColor = config.backgroundColor || '#ffffff';
  const titleColor = config.titleColor || '#000000';
  const messageColor = config.messageColor || '#666666';
  const cornerRadius = config.cornerRadius || 12;
  const primaryButtonColor = config.primaryButtonColor || typeColor;

  return (
    <div className="w-full h-full flex items-center justify-center bg-gray-900 p-4 relative" style={{ minWidth: 0, minHeight: 0 }}>
      <style>
        {`
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slide-in {
            from { transform: translateY(-20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @keyframes scale-in {
            from { transform: scale(0.9); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          @keyframes bounce-in {
            0% { transform: scale(0.3); opacity: 0; }
            50% { transform: scale(1.05); }
            70% { transform: scale(0.9); }
            100% { transform: scale(1); opacity: 1; }
          }
          .animate-fade-in {
            animation: fade-in ${animationDuration}s ease-out;
          }
          .animate-slide-in {
            animation: slide-in ${animationDuration}s ease-out;
          }
          .animate-scale-in {
            animation: scale-in ${animationDuration}s ease-out;
          }
          .animate-bounce-in {
            animation: bounce-in ${animationDuration * 1.67}s ease-out;
          }
        `}
      </style>

      {/* Background Blur */}
      {blurBackground && (
        <div
          className="absolute inset-0 bg-black opacity-50"
          style={{
            backdropFilter: `blur(${blurIntensity}px)`,
            WebkitBackdropFilter: `blur(${blurIntensity}px)`,
          }}
          onClick={() => {
            if (dismissible) {
              setShowAlert(false);
            }
          }}
        />
      )}

      {/* Alert Card */}
      {showAlert && (
        <div
          className={`relative z-10 max-w-md w-full ${getAnimationClass()}`}
          style={{
            backgroundColor: backgroundColor,
            borderRadius: `${cornerRadius}px`,
            padding: `${padding}px`,
            boxShadow: shadowEnabled
              ? `0 ${shadowRadius}px 25px -5px rgba(0, 0, 0, ${shadowOpacity}), 0 10px 10px -5px rgba(0, 0, 0, ${shadowOpacity * 0.5})`
              : 'none',
            maxWidth: 'calc(100% - 32px)',
            margin: '0 auto',
            boxSizing: 'border-box',
          }}
        >
          {/* Icon and Title */}
          <div className="flex items-center gap-3 mb-3">
            {showIcon && (
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: `${typeColor}20`,
                  color: typeColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  fontWeight: 'bold',
                }}
              >
                {typeIcon}
              </div>
            )}
            <h3
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: titleColor,
                flex: 1,
              }}
            >
              {title}
            </h3>
          </div>

          {/* Message */}
          <p
            style={{
              fontSize: '14px',
              color: messageColor,
              marginBottom: '20px',
              lineHeight: '1.5',
            }}
          >
            {message}
          </p>

          {/* Buttons */}
          <div className="flex gap-2 justify-end flex-wrap">
            {config.buttonCount >= 3 && (
              <button
                onClick={() => setShowAlert(false)}
                style={{
                  padding: `${config.tertiaryButtonPadding !== null && config.tertiaryButtonPadding !== undefined ? config.tertiaryButtonPadding : 8}px ${(config.tertiaryButtonPadding !== null && config.tertiaryButtonPadding !== undefined ? config.tertiaryButtonPadding : 8) * 2}px`,
                  borderRadius: `${config.tertiaryButtonCornerRadius !== null && config.tertiaryButtonCornerRadius !== undefined ? config.tertiaryButtonCornerRadius : 8}px`,
                  border: config.tertiaryButtonBorderWidth !== null && config.tertiaryButtonBorderWidth !== undefined && config.tertiaryButtonBorderWidth > 0
                    ? `${config.tertiaryButtonBorderWidth}px solid ${config.tertiaryButtonBorderColor || '#e5e7eb'}`
                    : '1px solid #e5e7eb',
                  backgroundColor: config.tertiaryButtonBackgroundColor || 'transparent',
                  color: config.tertiaryButtonTextColor || '#6b7280',
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: config.tertiaryButtonShadowEnabled
                    ? `0 ${config.tertiaryButtonShadowRadius || 4}px ${(config.tertiaryButtonShadowRadius || 4) * 2}px rgba(0, 0, 0, ${config.tertiaryButtonShadowOpacity ?? 0.2})`
                    : 'none',
                }}
              >
                {tertiaryButtonText}
              </button>
            )}
            {config.buttonCount >= 2 && (
              <button
                onClick={() => setShowAlert(false)}
                style={{
                  padding: `${config.secondaryButtonPadding !== null && config.secondaryButtonPadding !== undefined ? config.secondaryButtonPadding : 8}px ${(config.secondaryButtonPadding !== null && config.secondaryButtonPadding !== undefined ? config.secondaryButtonPadding : 8) * 2}px`,
                  borderRadius: `${config.secondaryButtonCornerRadius !== null && config.secondaryButtonCornerRadius !== undefined ? config.secondaryButtonCornerRadius : 8}px`,
                  border: config.secondaryButtonBorderWidth !== null && config.secondaryButtonBorderWidth !== undefined && config.secondaryButtonBorderWidth > 0
                    ? `${config.secondaryButtonBorderWidth}px solid ${config.secondaryButtonBorderColor || '#e5e7eb'}`
                    : '1px solid #e5e7eb',
                  backgroundColor: config.secondaryButtonBackgroundColor || 'transparent',
                  color: config.secondaryButtonTextColor || '#6b7280',
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: config.secondaryButtonShadowEnabled
                    ? `0 ${config.secondaryButtonShadowRadius || 4}px ${(config.secondaryButtonShadowRadius || 4) * 2}px rgba(0, 0, 0, ${config.secondaryButtonShadowOpacity ?? 0.2})`
                    : 'none',
                }}
              >
                {secondaryButtonText}
              </button>
            )}
            <button
              onClick={() => setShowAlert(false)}
              style={{
                padding: `${config.primaryButtonPadding !== null && config.primaryButtonPadding !== undefined ? config.primaryButtonPadding : 8}px ${(config.primaryButtonPadding !== null && config.primaryButtonPadding !== undefined ? config.primaryButtonPadding : 8) * 2}px`,
                borderRadius: `${config.primaryButtonCornerRadius !== null && config.primaryButtonCornerRadius !== undefined ? config.primaryButtonCornerRadius : 8}px`,
                backgroundColor: config.primaryButtonBackgroundColor || primaryButtonColor,
                color: config.primaryButtonTextColor || 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                border: config.primaryButtonBorderWidth !== null && config.primaryButtonBorderWidth !== undefined && config.primaryButtonBorderWidth > 0
                  ? `${config.primaryButtonBorderWidth}px solid ${config.primaryButtonBorderColor || 'transparent'}`
                  : 'none',
                boxShadow: config.primaryButtonShadowEnabled
                  ? `0 ${config.primaryButtonShadowRadius || 4}px ${(config.primaryButtonShadowRadius || 4) * 2}px rgba(0, 0, 0, ${config.primaryButtonShadowOpacity ?? 0.2})`
                  : 'none',
              }}
            >
              {primaryButtonText}
            </button>
          </div>
        </div>
      )}

      {!showAlert && (
        <button
          onClick={() => setShowAlert(true)}
          className="px-4 py-2 bg-gradient-primary bg-gradient-primary-hover rounded text-white text-sm"
        >
          Show Alert
        </button>
      )}
    </div>
  );
};

export default AlertPreview;
